import { ModifiedQuestion } from '@/client/quizzes';
import {
  RichTextActiveFormats,
  RichTextEditor,
  RichTextEditorHandle,
  RichTextToolbar,
} from '@/ui/rich-text';
import { isRtlLanguage } from '@/utils/helpers/language.helper';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { QuizQuestionMedia } from '../QuizQuestionMedia';

import { LanguagesEnum } from '@/api/enums';
import { ContentMedia, InteractiveElementType } from '@/client/courses';
import { ContentEmailComponent } from '@/components/courses/elements/ContentEmailComponent';
import { ContentSMSComponent } from '@/components/courses/elements/ContentSMSComponent';
import { ProgressHeader } from '@/ui/progress-header/ProgressHeader';
import { QuizAnswerRow } from './QuizAnswerRow';
import { QuizAddOptionButton, QuizMultiChoiceHint } from './QuizEditorUtils';

const CanvasContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
`;

// Clears the content below it by the same 36px the slides editor uses, less
// what the container's gap already contributes
const CanvasToolbar = styled(RichTextToolbar)`
  margin-bottom: calc(36px - var(--medium-padding));
`;

// Mirrors the learner-facing question layout in Quiz.tsx — text left, image right
const QuestionRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--default-padding);
`;

const QuestionColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding);
`;

const QuestionEditorWrapper = styled.div`
  flex: 1;

  .rich-text-editor-root {
    min-height: unset;
    height: auto;
    background: transparent;
    border: none;
    border-radius: 0;

    &:hover {
      border: none;
    }

    &.p-editor-focused {
      border: none;
      box-shadow: none;
    }

    .ql-container {
      border: none;
      font-size: var(--heading-small-font-size);
      font-family: var(--font-family);
      // The editor sets its own padding; the container adding more would put
      // the text off the placeholder, which is positioned against the container
      padding: 0;
    }

    .ql-editor {
      padding: 0;
      min-height: unset;
    }

    .ql-blank::before {
      left: 0;
    }
  }
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
`;

interface QuizEditorCanvasProps {
  questions: ModifiedQuestion[];
  currentQuestionIndex: number;
  translationKey: string;
  totalQuestions: number;
  toolbarProps: {
    applyFormat: (format: string, value?: unknown) => void;
    activeFormats: RichTextActiveFormats;
    isActive: boolean;
  };
  registerEditor: (handle: RichTextEditorHandle | null) => void;
  unregisterEditor: () => void;
  onQuestionTextChange: (value: string) => void;
  onAnswerTextChange: (answerIndex: number, value: string) => void;
  onToggleCorrect: (answerIndex: number) => void;
  onDeleteAnswer: (answerIndex: number) => void;
  onAddAnswer: () => void;
  onAnswerReorder: (oldIndex: number, newIndex: number) => void;
  onInteractiveElementDataChange: (data: unknown) => void;
  onMediaChange: (media: ContentMedia | null) => void;
}

export const QuizEditorCanvas: React.FC<QuizEditorCanvasProps> = ({
  questions,
  currentQuestionIndex,
  translationKey,
  totalQuestions,
  toolbarProps,
  registerEditor,
  unregisterEditor,
  onQuestionTextChange,
  onAnswerTextChange,
  onToggleCorrect,
  onDeleteAnswer,
  onAddAnswer,
  onAnswerReorder,
  onInteractiveElementDataChange,
  onMediaChange,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<RichTextEditorHandle>(null);
  const [autoFocusAnswerIndex, setAutoFocusAnswerIndex] = useState<
    number | null
  >(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const currentQuestion = questions[currentQuestionIndex];
  const currentTranslation = currentQuestion?.translations[translationKey];
  const questionText = currentTranslation?.text ?? '';
  const media = currentTranslation?.media;
  const interactiveElement = currentTranslation?.interactiveElement;
  const questionId = currentQuestion?.id ?? `question-${currentQuestionIndex}`;

  const correctCount = useMemo(
    () => currentQuestion?.answerOptions.filter((a) => a.isCorrect).length ?? 0,
    [currentQuestion?.answerOptions],
  );

  const sortableIds = useMemo(
    () =>
      currentQuestion?.answerOptions.map((_, index) => `answer-${index}`) ?? [],
    [currentQuestion?.answerOptions],
  );

  const handleQuestionFocus = () => {
    registerEditor(editorRef.current);
  };

  const handleAddAnswerWithFocus = useCallback(() => {
    setAutoFocusAnswerIndex(currentQuestion?.answerOptions?.length ?? 0);
    onAddAnswer();
  }, [onAddAnswer, currentQuestion?.answerOptions?.length]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = Number(String(active.id).replace('answer-', ''));
    const newIndex = Number(String(over.id).replace('answer-', ''));
    if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;

    onAnswerReorder(oldIndex, newIndex);
  };

  if (!currentQuestion) return null;

  return (
    <CanvasContainer translate="no">
      <CanvasToolbar
        activeFormats={toolbarProps.activeFormats}
        disabled={!toolbarProps.isActive}
        onFormat={toolbarProps.applyFormat}
        linkPromptLabel={t('content.richText.linkPrompt')}
      />
      <ProgressHeader
        currentIndex={currentQuestionIndex}
        totalCount={totalQuestions}
        type="quiz"
        attemptLanguage={translationKey as LanguagesEnum}
      />
      <QuestionRow>
        <QuestionColumn>
          <QuestionEditorWrapper>
            <RichTextEditor
              key={`question-${
                currentQuestion?.id ??
                currentQuestion?.clientKey ??
                `idx-${currentQuestionIndex}`
              }-${translationKey}`}
              ref={editorRef}
              defaultValue={questionText}
              placeholder={t('content.question.text.placeholder')}
              onChange={onQuestionTextChange}
              onFocus={handleQuestionFocus}
              onBlur={unregisterEditor}
            />
          </QuestionEditorWrapper>
          <QuizMultiChoiceHint
            show={correctCount > 1}
            language={translationKey}
          />
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              <AnswerList>
                {currentQuestion.answerOptions.map((answer, index) => (
                  <QuizAnswerRow
                    key={answer.id ?? answer.clientKey ?? `answer-${index}`}
                    id={`answer-${index}`}
                    answer={answer}
                    answerIndex={index}
                    isCorrect={!!answer.isCorrect}
                    translationKey={translationKey}
                    autoFocus={autoFocusAnswerIndex === index}
                    onAutoFocusHandled={() => setAutoFocusAnswerIndex(null)}
                    onToggleCorrect={onToggleCorrect}
                    onChangeText={onAnswerTextChange}
                    onDelete={onDeleteAnswer}
                    registerEditor={registerEditor}
                    unregisterEditor={unregisterEditor}
                  />
                ))}
                <QuizAddOptionButton onClick={handleAddAnswerWithFocus} />
              </AnswerList>
            </SortableContext>
          </DndContext>
        </QuestionColumn>
        {interactiveElement?.type === InteractiveElementType.EMAIL && (
          <ContentEmailComponent
            id={questionId}
            interactiveElement={interactiveElement}
            onUpdate={onInteractiveElementDataChange}
          />
        )}
        {interactiveElement?.type === InteractiveElementType.SMS && (
          <ContentSMSComponent
            id={questionId}
            interactiveElement={interactiveElement}
            rtl={isRtlLanguage(translationKey as LanguagesEnum)}
            onUpdate={onInteractiveElementDataChange}
          />
        )}
        {!interactiveElement && media?.file?.signedUrl && (
          <QuizQuestionMedia media={media} onChange={onMediaChange} />
        )}
      </QuestionRow>
    </CanvasContainer>
  );
};
